import React, { useState } from 'react';
import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

interface ClickToCopyProps {
  text: string;
  displayText?: string;
  className?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  showIconOnHover?: boolean;
}

export default function ClickToCopy({ 
  text, 
  displayText, 
  className = '', 
  iconSize = 'sm',
  showIconOnHover = true 
}: ClickToCopyProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      // First try the modern clipboard API (requires HTTPS)
      if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      
      // Fallback to deprecated execCommand for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          throw new Error('execCommand failed');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const iconClass = iconSizeClasses[iconSize];

  return (
    <button
      onClick={(e) => { e.stopPropagation(); void copyToClipboard(); }}
      className={`flex items-center space-x-1 transition-colors group ${className}`}
      title="Click to copy"
    >
      <span>{displayText || text}</span>
      {copied ? (
        <CheckIcon className={`${iconClass} text-green-500`} />
      ) : (
        <DocumentDuplicateIcon className={`${iconClass} ${showIconOnHover ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} transition-opacity`} />
      )}
    </button>
  );
}
